import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { XStack, YStack } from 'tamagui'
import { lazy as loadable } from '@loadable/component'
import { useScript } from 'hoofd'

import { buildBreadcrumbList, HOME_BREADCRUMB } from '@newComponents/buildBreadcrumbList'
import { Grid } from '@newComponents/Grid'
import { Group } from '@newComponents/Group'
import { Icon } from '@newComponents/Icon'
import { IconWrapper } from '@newComponents/Icon/IconWrapper'
import { Link } from '@newComponents/Link'
import { Metadata } from '@newComponents/Metadata'
import { MobileNavDropdown } from '@newComponents/MobileNavDropdown'
import { GradientBackgroundScreen } from '@newComponents/Screen'
import { H1, Paragraph, Text } from '@newComponents/Text'
import { HOST_NAME } from '@data/config/url'

import { DOC_SECTIONS } from './docRoutes'

const NavigationIcon = loadable(
  () => import('@newComponents/Icon/icons/NavigationIcon')
)

const DocsBackLink = () => (
  <Link href='/docs' hoverStyle={{ textDecorationLine: 'none' }}>
    <XStack alignItems='center' gap='$3' paddingLeft='$5'>
      <Icon name='arrow-left' color='$color' />
      <Text fontWeight='$6' color='$color'>
        Documentation
      </Text>
    </XStack>
  </Link>
)

const SectionSidebar = ({
  section,
  activePage,
}: {
  section: string
  activePage: string
}) => {
  const sectionConfig = DOC_SECTIONS.find((s) => s.path === section)
  const pages = sectionConfig?.pages ?? []

  const activeIndex = useMemo(() => {
    const index = pages.findIndex((p) => p.path === activePage)
    return index >= 0 ? index : undefined
  }, [pages, activePage])

  return (
    <Group
      tag='nav'
      activeIndex={activeIndex}
      gap='$2'
      fullWidthHighlight
      highlightedElementProps={{ borderRadius: '$6' }}
    >
      {pages.map((page, index) => {
        const isActive = page.path === activePage
        return (
          <Group.Item key={page.path} index={index} cursor='pointer'>
            <Link
              href={`/docs/${section}/${page.path}`}
              hoverStyle={{ textDecorationLine: 'none' }}
              paddingHorizontal='$5'
              paddingVertical='$2'
            >
              <XStack alignItems='center' gap='$3'>
                <Text
                  color={isActive ? '$colorStrong' : '$color'}
                  fontWeight={isActive ? '$6' : '$5'}
                >
                  {page.label}
                </Text>
              </XStack>
            </Link>
          </Group.Item>
        )
      })}
    </Group>
  )
}

export const DocsSection = ({
  section,
  children,
}: {
  section: string
  children: React.ReactNode
}) => {
  const location = useLocation()
  const sectionConfig = DOC_SECTIONS.find((s) => s.path === section)

  // Extract the active page from the URL pathname
  // For /docs/{section}/{page}, extract {page}; default to 'overview' at section root
  const activePage = useMemo(() => {
    const prefix = `/docs/${section}/`
    if (location.pathname.startsWith(prefix)) {
      const remaining = location.pathname.slice(prefix.length).replace(/\/$/, '')
      if (remaining) return remaining
    }
    return 'overview'
  }, [location.pathname, section])

  const activePageConfig = sectionConfig?.pages.find((p) => p.path === activePage)
  const currentLabel = activePageConfig?.label ?? sectionConfig?.label ?? 'Documentation'
  const pageTitle = activePageConfig?.label ?? ''
  const pageDescription = sectionConfig?.description ?? ''

  // Scroll to top on sub-page navigation
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  const breadcrumbs = useMemo(
    () =>
      buildBreadcrumbList([
        HOME_BREADCRUMB,
        { name: 'Documentation', url: `${HOST_NAME}/docs` },
        { name: sectionConfig?.label ?? '', url: `${HOST_NAME}/docs/${section}` },
        { name: activePageConfig?.label ?? '', url: `${HOST_NAME}/docs/${section}/${activePage}` },
      ]),
    [section, activePage, sectionConfig?.label, activePageConfig?.label]
  )
  useScript({ type: 'application/ld+json', text: breadcrumbs })

  const sidebar = <SectionSidebar section={section} activePage={activePage} />
  const backLink = <DocsBackLink />

  const content = (
    <YStack tag='main' gap='$6'>
      <YStack gap='$2' paddingHorizontal='$5'>
        <H1>{pageTitle}</H1>
        <Paragraph size='$5' color='$colorMuted'>
          {pageDescription}
        </Paragraph>
      </YStack>

      <YStack gap='$6' tag='article'>
        {children}
      </YStack>

      <XStack justifyContent='flex-start' paddingHorizontal='$5'>
        <Link
          href={`mailto:hi@guild.host?subject=${encodeURIComponent('Documentation Issue: ' + pageTitle)}`}
          hoverStyle={{ textDecorationLine: 'none' }}
        >
          <XStack alignItems='center' gap='$2'>
            <Icon name='alert' size='$4' color='$colorMuted' />
            <Paragraph size='$4' color='$colorMuted'>
              Report a Documentation Issue
            </Paragraph>
          </XStack>
        </Link>
      </XStack>
    </YStack>
  )

  const metaTitle = activePageConfig
    ? `${activePageConfig.label} - ${sectionConfig?.label ?? ''} - Guild Documentation`
    : `${sectionConfig?.label ?? ''} - Guild Documentation`

  return (
    <>
      <Metadata
        title={metaTitle}
        description={pageDescription}
        canonicalUrl={`${HOST_NAME}/docs/${section}/${activePage}`}
      />
      <GradientBackgroundScreen>
        {/* Mobile: stacked layout */}
        <YStack
          gap='$4'
          $gtSm={{ display: 'none' }}
          maxWidth={1200}
          marginHorizontal='auto'
          width='100%'
        >
          {backLink}

          <MobileNavDropdown
            label={currentLabel}
            openLabel={sectionConfig?.label ?? 'Documentation'}
            icon={
              <IconWrapper color='$color'>
                <NavigationIcon />
              </IconWrapper>
            }
          >
            {sidebar}
          </MobileNavDropdown>

          {content}
        </YStack>

        {/* Desktop: two-column Grid layout */}
        <Grid
          gap='$12'
          display='none'
          $gtSm={{ display: 'flex' }}
          maxWidth={1200}
          marginHorizontal='auto'
          width='100%'
        >
          <Grid.Column flex={3} $gtMd={{ flex: 2 }}>
            <YStack
              style={{ position: 'sticky', top: 116 }}
              alignSelf='flex-start'
              gap='$10'
            >
              {backLink}
              {sidebar}
            </YStack>
          </Grid.Column>

          <Grid.Column flex={9} $gtMd={{ flex: 10 }}>
            {content}
          </Grid.Column>
        </Grid>
      </GradientBackgroundScreen>
    </>
  )
}
